import { activate as activateElement } from 'OK/capture';
import logger from 'OK/logger';
import { trigger, ajax } from 'OK/utils/vanilla';
import throttle from 'OK/utils/throttle';

var LOADER_VIEWPORT_THRESHOLD = 0.5;

/**
 * Listener that will catch ajax done
 * @this {Loader} loader
 */
 function doneCallback(data) {
    var response = data.response,
        xhr = data.xhr,
        list = this.list,
        last = list.lastElementChild,
        newChild,
        addedElements = [];

    function finishLoad() {
        this.hookElement.classList.remove('in-progress');
        this.loading = false;
        trigger(this.hookElement, 'dataload', { elements : addedElements});
        this.loadNextPageIfNoScrollAndAutoMode();
    }

    if (xhr.getResponseHeader('fetchedAll') === 'true') {
        this.deactivate();
    }
    this.lastElement = xhr.getResponseHeader('lastelem');

    if (response === null || response.length === 0) {
        finishLoad.call(this);
        return addedElements;
    }

    list.insertAdjacentHTML('beforeend', response);

    if (OK.hookModel.dispatchDataLoadedEvent) {
        OK.hookModel.events.dispatchDataLoadedEvent();
    }

    // В нулевой момент времени список мог быть пустым, тогда last === null
    // В этом случае берем первый узел в списке — мы его только что обновили
    newChild = last === null
        ? list.firstElementChild
        : last.nextElementSibling;

    while (newChild !== null) {
        activateElement(newChild);
        addedElements.push(newChild);
        newChild = newChild.nextElementSibling;
    }

    this.page = this.page + 1;
    this.hookElement.setAttribute('data-page', this.page);
    this.autoPage = this.autoPage + 1;

    if (this.isManual) {
        if (!this.isDeactivated) {
            this.hookElement.classList.remove('loader-container');
        }
        this.switchToAuto();
    } else {
        this.switchToManual();
    }

    finishLoad.call(this);

    return addedElements;
}

function getDocumentHeight() {
    var body = document.body,
        html = document.documentElement;

    return Math.max(body.scrollHeight, body.offsetHeight,
        html.clientHeight, html.scrollHeight, html.offsetHeight);
}

export default class Loader {
    constructor() {
        this.loading = false;
    }

    handleEvent(e) {
        if (!document.body.contains(this.hookElement)) {
            logger.error('loader', 'oldQuery');
            this.deactivate();
            return;
        }
        switch (e.type) {
            case 'click':
                if (!this.loading) {
                    this.hookElement.classList.add('in-progress');
                    this.hookElement.classList.add('loader-container');
                    this.loadMore();

                }
                e.preventDefault();
                break;
            case 'scroll':
                if (!this.loading) {
                    this.loadNextPageIfNeed();
                }
                break;
        }
    }

    activate(element) {
        this.hookElement = element;
        var containerClass = this.hookElement.getAttribute('data-list-container-class');
        this.list = (containerClass && this.hookElement.getElementsByClassName(containerClass)[0]) || this.hookElement.firstElementChild;
        this.showMoreButton = this.hookElement.getElementsByClassName('js-show-more')[0];
        this.url = this.hookElement.getAttribute('data-url');
        this.params = this.hookElement.getAttribute('data-params');
        this.dynamic = this.hookElement.getAttribute('data-dynamic');
        this.forceBottomEdge = this.hookElement.getAttribute('data-forcedbottomedge') || 0;
        this.dataMax = this.hookElement.getAttribute('data-max') || 0;
        this.manualPagesCount = this.hookElement.getAttribute('data-manualpagescount') || 0;
        this.scrollingContainerId = this.hookElement.getAttribute('data-scroller');
        this.staticParams = this.params ? this.params.split('&') : [];
        this.dynamicParams = this.dynamic ? this.dynamic.split(',') : [];
        this.lastElement = this.hookElement.getAttribute('data-last-element');
        this.pageParamsName = this.hookElement.getAttribute('data-pageParam') || 'st.page';
        this.checkViewport = element.getAttribute('data-check-viewport') === '1';
        var pageAttr = this.hookElement.getAttribute('data-page');
        if (pageAttr) {
            this.page = parseInt(pageAttr, 10);
        } else {
            this.page = 1;
        }
        this.autoPage = this.page;
        this.isManual = false;
        this.isDeactivated = false;

        this.throttledHandleEvent = throttle(200, this.handleEvent.bind(this));

        if (this.scrollingContainerId) {
            this.scrollingContainer = document.getElementById(this.scrollingContainerId);
        }

        if (!this.scrollingContainer) {
            this.scrollingContainer = window;
        }

        this.switchToManual();

        if (!this.isManual) {
            this.scrollingContainer.addEventListener('scroll', this.throttledHandleEvent, false);
        }

        Object.defineProperty(this.hookElement, 'getLoader', {
            enumerable: false,
            configurable: false,
            writable: false,
            value: function () {
                return this;
            }.bind(this)
        });

        this.loadNextPageIfNoScrollAndAutoMode();
    }

    loadNextPageIfNoScrollAndAutoMode() {
        if (this.checkViewport && !this.isManual && !this.loading) {
            var scrollingContainer = this.scrollingContainer === window ? document.body : this.scrollingContainer;
            if (scrollingContainer.scrollHeight === scrollingContainer.offsetHeight) {
                this.loadNextPageIfNeed();
            }
        }
    }

    deactivate() {
        this.scrollingContainer && this.scrollingContainer.removeEventListener('scroll', this.throttledHandleEvent, false);
        this.showMoreButton && this.showMoreButton.removeEventListener('click', this.throttledHandleEvent, false);
        this.hookElement.classList.add('loader-container', 'loader__done');
        this.isDeactivated = true;
    }

    loadNextPageIfNeed() {
        if (isScrollLowEnough.call(this)) {
            this.hookElement.classList.add('in-progress');
            this.loadMore();
        }
    }

    switchToManual() {
        function isManualPagesCountLimitExceeded() {
            return this.page > this.autoPage && this.autoPage > this.manualPagesCount;
        }

        function isInitialAutoLoadLimitExceeded() {
            return this.page == this.autoPage && this.page > this.dataMax;
        }

        if (!this.isManual && !this.isDeactivated) {
            if (isInitialAutoLoadLimitExceeded.call(this)
                || isManualPagesCountLimitExceeded.call(this)) {
                this.hookElement.classList.remove('loader-container');

                this.scrollingContainer.removeEventListener('scroll', this.throttledHandleEvent, false);
                this.showMoreButton.addEventListener('click', this.throttledHandleEvent, false);

                this.isManual = true;
            }
        }
    }

    switchToAuto() {
        if (this.manualPagesCount > 1 && this.isManual && !this.isDeactivated) {
            this.hookElement.classList.add('loader-container');

            this.scrollingContainer.addEventListener('scroll', this.throttledHandleEvent, false);
            this.showMoreButton.removeEventListener('click', this.throttledHandleEvent, false);

            this.isManual = false;
            this.autoPage = 1;
        }
    }

    loadMore(data) {
        // После долистывания до конца списка лоадер деактивируется
        // Запретим какие-либо запросы после этого — они могли остаться со скролла
        if (this.isDeactivated) {
            this.hookElement.classList.remove('in-progress');
            return Promise.resolve([]);
        }

        this.loading = true;

        data = Object.assign({'fetch': this.isManual}, data);

        var last = this.list.lastElementChild, requestUrl = this.url, staticParams = this.staticParams,
            dynamicParams = this.dynamicParams, lastElement = this.lastElement, index, parts, elem;

        data[this.pageParamsName] = this.page + 1;

        if (staticParams.length) {
            /* переносим параметры вида key=value в data */
            for (index = 0; index < staticParams.length; index += 1) {
                elem = staticParams[index];
                parts = elem.split('=');
                if (parts.length === 2) {
                    data[parts[0]] = parts[1];
                }
            }
        }

        if (dynamicParams.length) {
            for (index = 0; index < dynamicParams.length; index += 1) {
                elem = dynamicParams[index];
                data[elem] = last.getAttribute(elem);
            }
        }

        if (lastElement) {
            data['st.lastelem'] = lastElement;
        }

        var request = ajax({
            url: requestUrl,
            data: data
        });

        return request.then(doneCallback.bind(this));
    }
}

/**
 * @returns {boolean}
 */
function isScrollLowEnough() {
    if (this.forceBottomEdge || this.scrollingContainer !== window) {
        if (this.scrollingContainerId && this.scrollingContainer !== window) {
            return this.scrollingContainer.scrollTop + this.scrollingContainer.offsetHeight > this.scrollingContainer.scrollHeight - this.forceBottomEdge;
        }
        return window.pageYOffset > getDocumentHeight() - document.documentElement.clientHeight - this.forceBottomEdge;
    }
    var hookElementRect = this.hookElement.getBoundingClientRect();
    var hookElementVisiblePart = window.innerHeight - hookElementRect.top;
    var hookElementHiddenPart = hookElementRect.height - hookElementVisiblePart;
    // Будем подгружать, если невидимая часть будет меньше, чем доля вьюпорта
    return hookElementHiddenPart < (window.innerHeight * LOADER_VIEWPORT_THRESHOLD);
}
